import {
  Box, Grid,
} from '@mui/material';
import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockData from '../../../../models/BlockData';
import BlockProps from '../../../../models/BlockProps';
import LinkData from '../../../../models/LinkData';
import BlockHeaderLogoMenuData from './BlockHeaderLogoMenuData';
import CloseButton from '../../../Buttons/Close';
import Utils from '../../../../utils/Utils';
import styles from './BlockHeaderLogoMenu.module.css';
import HeaderMenu from '../shared/HeaderMenu';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import ImageModal from '../../../Modals/ImageModal/ImageModal';
import ImageComponent from '../../../ImageComponent';
import { ImagesContext } from '../../../../contexts/ImagesContext';
import useResponsive from '../../../../hooks/UseResponsive';

export interface BlockHeaderLogoMenuProps extends BlockProps {
  blockData: BlockData[]
}

function BlockHeaderLogoMenu({
  data, isEditing, isTemplate, editHandler, blockData,
}: BlockHeaderLogoMenuProps) {
  const props = (data.data as BlockHeaderLogoMenuData);
  const { images } = useContext(ImagesContext);
  const hamburgerCheckbox = useRef(null);

  const [open, setOpen] = useState(false);

  useResponsive({
    items: [
      {
        wrapperId: 'logoMenuWrapper',
        targetId: 'logoHeaderHamburger',
        queries: [
          { size: 0, className: styles.flex },
          { size: 344, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'logoMenuWrapper',
        targetId: 'logoHeaderHamburgerMenu',
        queries: [
          { size: 0, className: styles.flex },
          { size: 344, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'logoMenuWrapper',
        targetId: 'logoHeaderMenu',
        queries: [
          { size: 0, className: styles.hidden },
          { size: 344, className: styles.flex },
        ],
      },
    ],
  });

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const hamburgerButton = useMemo(() => (
    <Grid id="logoHeaderHamburger" item xs="auto">
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={styles['show-menu']}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const hamburgerMenu = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid
          id="logoHeaderHamburgerMenu"
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          sx={{
            p: 1.5,
            backgroundColor: '#f5f7fb',
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <HeaderMenu
              menus={props.menus}
              isEditing={isEditing}
              direction="column"
              handleMenuDelete={handleMenuDelete}
              handleOpen={handleOpen}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }, [handleMenuDelete, handleOpen, isEditing, props.menus]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid id="logoHeaderMenu" sx={{ width: 'auto' }} container justifyContent="center" alignItems="center">
          <HeaderMenu
            menus={props.menus}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }
    return [];
  }, [props.menus, isEditing, handleOpen, handleMenuDelete]);

  useEffect(() => {
    if (isEditing) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={styles.header}
        width="100%"
      >
        <Grid container justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
          {props.logo ? (
            <Grid item xs={6} sx={Utils.hoverBorder(isEditing)}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.logo = undefined;
                  editHandler(data.id, props);
                }}
              />
              <Box sx={{ height: '110px' }}>
                <ImageModal
                  disabled={isTemplate || !isEditing}
                  images={images}
                  handleOnConfirm={(base64image: string) => {
                    props.logo.value = base64image;
                    editHandler(data.id, props);
                  }}
                >
                  <ImageComponent src={props.logo.value} />
                </ImageModal>
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}
          <Grid item xs={6} sx={{ paddingLeft: props.logo ? 1.5 : 'none' }}>
            <Box
              id="logoMenuWrapper"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {hamburgerButton}
              {menuItems}
            </Box>
          </Grid>

        </Grid>

        <input type="checkbox" id={styles['show-menu']} ref={hamburgerCheckbox} />
        <Box id={styles['hamburger-menu']} sx={{ width: '100%' }}>
          {hamburgerMenu}
          {!isEditing && (
            <Box className={styles.fadeDiv} />
          )}
        </Box>

        <HeaderMenuSettingsModal
          menuItems={props.menus ?? []}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box className={styles.fadeDiv} />
      )}
    </>
  );
}

export default BlockHeaderLogoMenu;
